/**
 * Team Hierarchy View — ConstellationView adapter for team hierarchy
 *
 * DRR-178: Replaces TeamOrgChart with the domain-agnostic ConstellationView.
 * Transforms flat GqlTeam[] into ConstellationViewNode[] + ConstellationViewEdge[]
 * with CONTAINS edge type for parent→child relationships.
 *
 * The constellation is centered on the root team (no parentTeamId).
 * Child teams appear in Ring 1, grandchildren in Ring 2, etc.
 */

import { useMemo } from 'react';
import { Text, Paper } from '@mantine/core';
import {
  ConstellationView,
  type ConstellationViewNode,
  type ConstellationViewEdge,
} from '@ferp/workflow-forms';

interface TeamData {
  readonly id: string;
  readonly name: string;
  readonly teamType?: string | null;
  readonly parentTeamId?: string | null;
  readonly memberCount?: number | null;
  readonly leadActorId?: string | null;
}

interface TeamHierarchyViewProps {
  readonly teams: TeamData[];
  readonly onTeamClick?: (teamId: string) => void;
  readonly height?: number;
}

export function TeamHierarchyView({ teams, onTeamClick, height = 500 }: TeamHierarchyViewProps) {
  const { center, nodes, edges } = useMemo(() => {
    if (teams.length === 0) {
      return { center: null, nodes: [], edges: [] };
    }

    // Build parent→children map for ring assignment
    const childrenOf = new Map<string | null, TeamData[]>();
    for (const team of teams) {
      const parentId = team.parentTeamId ?? null;
      if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
      childrenOf.get(parentId)!.push(team);
    }

    // Find root teams (no parent)
    const roots = childrenOf.get(null) ?? [];
    // teams.length > 0 guaranteed by early return above
    const centerTeam = roots[0] ?? teams[0]!;

    // BFS to assign rings
    const ringMap = new Map<string, number>();
    const queue: Array<{ id: string; ring: number }> = [{ id: centerTeam.id, ring: 0 }];
    // Other roots at ring 1
    for (const root of roots) {
      if (root.id !== centerTeam.id) {
        queue.push({ id: root.id, ring: 1 });
      }
    }

    while (queue.length > 0) {
      const { id, ring } = queue.shift()!;
      if (ringMap.has(id)) continue;
      ringMap.set(id, ring);
      const children = childrenOf.get(id) ?? [];
      for (const child of children) {
        queue.push({ id: child.id, ring: ring + 1 });
      }
    }

    const constellationNodes: ConstellationViewNode[] = teams.map((team) => ({
      nodeId: team.id,
      name: team.name,
      role: team.teamType ?? 'TEAM',
      ring: ringMap.get(team.id) ?? 1,
      metadata: {
        memberCount: team.memberCount ?? 0,
        leadActorId: team.leadActorId ?? '',
      },
    }));

    const constellationEdges: ConstellationViewEdge[] = teams
      .filter((team) => team.parentTeamId)
      .map((team) => ({
        sourceId: team.parentTeamId!,
        targetId: team.id,
        edgeType: 'CONTAINS',
        label: 'contains',
      }));

    const centerNode = constellationNodes.find((n) => n.nodeId === centerTeam.id) ?? constellationNodes[0];

    return { center: centerNode, nodes: constellationNodes, edges: constellationEdges };
  }, [teams]);

  if (!center || nodes.length === 0) {
    return (
      <Paper p="xl" ta="center" c="dimmed">
        <Text>No teams to display</Text>
      </Paper>
    );
  }

  return (
    <ConstellationView
      center={center}
      nodes={nodes}
      edges={edges}
      onNodeSelect={onTeamClick}
      height={height}
    />
  );
}
